<template>
  <PropertyDetailsCard :title="title" :subtitle="subtitle" :tone="tone">
    <template v-if="titleBadge" #badge>
      <Badge variant="grey" size="small" :label="titleBadge" />
    </template>
    <template v-if="$slots.subtitle" #subtitle>
      <slot name="subtitle" />
    </template>
    <!-- An empty card's only action is its placeholder's button -->
    <template v-if="hasInfo" #action>
      <PropertyDetailsCardEditButton @click="showModal" />
    </template>

    <slot v-if="hasInfo" :open="showModal" />
    <PropertyDetailsCardPlaceholder v-else-if="placeholder" v-bind="placeholder" @button-clicked="showModal" />

    <BuildingSectionSheet ref="sheetRef" :title="title" :hint="hint" :form="form" :form-props="formProps" />
  </PropertyDetailsCard>
</template>

<script setup lang="ts">
import { type Component, ref } from 'vue';

import { Badge } from '@tallo/design-system';
import { PropertyDetailsCard, PropertyDetailsCardEditButton, PropertyDetailsCardPlaceholder } from '@tallo/property';

import BuildingSectionSheet from './building-section-sheet.vue';

/** What an empty card says and offers in the rows' place */
export interface SectionPlaceholder {
  title: string;
  subtitle: string;
  buttonText: string;
}

/** One section of the building: its info, or a placeholder, and an edit sheet hosting the same form a property uses */
withDefaults(
  defineProps<{
    title: string;
    /** The line under the title, unless the `subtitle` slot gives one */
    subtitle?: string;
    /** Secondary sets apart what is only a default for new units */
    tone?: 'primary' | 'secondary';
    /** A state worth seeing without opening the form, such as an address hidden on platforms */
    titleBadge?: string;
    /** Said again at the top of the sheet, for what a save does and does not reach */
    hint?: string;
    form: Component;
    /** Props for the form, e.g. which part of the building basics a card edits */
    formProps?: Record<string, unknown>;
    hasInfo: boolean;
    /** Shown while there is no info; a card that always has some needs none */
    placeholder?: SectionPlaceholder;
  }>(),
  {
    subtitle: undefined,
    tone: 'primary',
    titleBadge: undefined,
    hint: undefined,
    formProps: undefined,
    placeholder: undefined,
  },
);

const sheetRef = ref<InstanceType<typeof BuildingSectionSheet>>();

function showModal() {
  sheetRef.value?.show();
}
</script>
